import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { formatCurrency, formatNumber, formatDate, formatDateTime } from '../regureStyles';
import { auditActionLabel } from '../../utils/auditPresentation';
import useConfirm from '../../components/ConfirmDialog/useConfirm';
import { useFeature } from '../../hooks/useAccess';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

// D-22: Documents tab was a placeholder. Real implementation lists
// FileExchange rows scoped to the policy, supports drag/click upload
// (POST /policies/:id/documents), and delete.
// Why the policy document was not produced, from Policy.documentGeneration
// (recorded at conversion and on every regenerate). Every reason has its own
// key in fr / en / de — see policyDocReason in the locale files.
function policyDocMessage(outcome, t, currency) {
  if (!outcome || outcome.status === 'generated' || !outcome.reason) return null;
  return t(`screens.policiesScreen.detail.policyDocReason.${outcome.reason}`, {
    policyPremium: outcome.policyPremium != null ? formatCurrency(outcome.policyPremium, currency) : '',
    ratedPremium:  outcome.ratedPremium  != null ? formatCurrency(outcome.ratedPremium,  currency) : '',
    message: outcome.message || '',
  });
}

function DocumentsTab({ policyId, policy, S, t }) {
  const [docs, setDocs] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [genOutcome, setGenOutcome] = useState(policy?.documentGeneration || null);
  useEffect(() => { setGenOutcome(policy?.documentGeneration || null); }, [policy?.documentGeneration]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const { confirm, ConfirmPortal } = useConfirm();

  const fetchDocs = useCallback(async () => {
    if (!policyId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${BASE_URL}/policies/${policyId}/documents`, { withCredentials: true });
      setDocs(Array.isArray(r.data?.data) ? r.data.data : []);
      setError('');
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [policyId]);
  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${BASE_URL}/policies/${policyId}/documents`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await fetchDocs();
    } catch (e2) {
      setError(e2.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (docId, name) => {
    const ok = await confirm({
      title: t('screens.policiesScreen.detail.deleteDocTitle', { defaultValue: 'Delete document?' }),
      message: t('screens.policiesScreen.detail.confirmDeleteDoc', {
        defaultValue: `"${name}" will be permanently removed from this policy. An audit entry will be recorded.`,
        name,
      }),
      confirmText: t('common.delete', { defaultValue: 'Delete' }),
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await axios.delete(`${BASE_URL}/policies/${policyId}/documents/${docId}`, { withCredentials: true });
      setDocs(prev => prev.filter(d => d._id !== docId));
    } catch (e) {
      window.alert(e.response?.data?.message || 'Delete failed');
    }
  };

  // Regenerate the policy document. Only offered for a policy converted from
  // an offer — that is where its content comes from.
  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    try {
      const r = await axios.post(`${BASE_URL}/policies/${policyId}/generate-document`, {}, { withCredentials: true });
      setGenOutcome(r.data?.data || null);
      await fetchDocs();
    } catch (e) {
      setError(e.response?.data?.message || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };
  const genMessage = policyDocMessage(genOutcome, t, policy?.currency);

  if (loading) return <div style={{ padding: 30, textAlign: 'center', color: S.textMuted, fontSize: 13 }}>Loading…</div>;

  return (
    <div style={{ padding: '20px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: S.textPrimary }}>
          {t('screens.policiesScreen.detail.documentsHeader', { defaultValue: 'Documents' })} ({docs.length})
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
        {policy?.offer && (
          <button type='button' onClick={handleGenerate} disabled={generating}
            style={{ padding: '8px 14px', background: '#fff', color: '#800020', border: '1px solid #800020', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: generating ? 'wait' : 'pointer' }}>
            {generating ? t('screens.policiesScreen.detail.generatingPolicyDoc') : t('screens.policiesScreen.detail.generatePolicyDoc')}
          </button>
        )}
        <label style={{ padding: '8px 14px', background: '#800020', color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: uploading ? 'wait' : 'pointer' }}>
          {uploading ? t('screens.policiesScreen.detail.uploading', { defaultValue: 'Uploading…' }) : t('screens.policiesScreen.detail.uploadDoc', { defaultValue: 'Upload' })}
          <input type='file' onChange={handleUpload} disabled={uploading} style={{ display: 'none' }} />
        </label>
        </div>
      </div>
      {genMessage && <div role='status' style={{ padding: 10, background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{genMessage}</div>}
      {error && <div style={{ padding: 10, background: '#fef2f2', color: '#991b1b', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{error}</div>}
      {docs.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: S.textMuted, border: `1px dashed ${S.borderLight}`, borderRadius: 10, fontSize: 13 }}>
          {t('screens.policiesScreen.detail.noDocuments', { defaultValue: 'No documents yet.' })}
        </div>
      ) : (
        <div style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 10, overflow: 'hidden' }}>
          {docs.map((d, i) => (
            <div key={d._id} style={{ padding: '12px 16px', borderBottom: i < docs.length - 1 ? `1px solid ${S.borderLight}` : 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: S.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.originalName || d.name}</div>
                <div style={{ fontSize: 11, color: S.textMuted, marginTop: 2 }}>{d.fileSize} · {d.uploadedAt ? formatDate(d.uploadedAt) : ''}</div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                {(d.fileUrl || d.s3Key) && (
                  <a href={`/api/v1/documents/${d._id}/view`} target='_blank' rel='noreferrer' style={{ fontSize: 13, color: '#800020', textDecoration: 'underline' }}>
                    {t('screens.policiesScreen.detail.viewDoc', { defaultValue: 'View' })}
                  </a>
                )}
                <button type='button' onClick={() => handleDelete(d._id, d.originalName || d.name)} style={{ fontSize: 13, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
                  {t('screens.policiesScreen.detail.deleteDoc', { defaultValue: 'Delete' })}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {ConfirmPortal}
    </div>
  );
}

const STATE_NAMES = {
  CA: 'California', TX: 'Texas', FL: 'Florida', AZ: 'Arizona',
  CO: 'Colorado', NV: 'Nevada', OR: 'Oregon', ID: 'Idaho',
  NM: 'New Mexico', UT: 'Utah', NY: 'New York', IL: 'Illinois',
  WA: 'Washington', PA: 'Pennsylvania',
};

const TabContent = ({history, S, activeTab, policy, lob }) => {
  const commissionsEnabled = useFeature('commissions');
  const { t } = useTranslation();
  function Section({ title, children }) {
    return (
      <div style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 14, padding: 24 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: S.textPrimary, marginBottom: 16, paddingBottom: 12, borderBottom: `1px solid ${S.borderLight}` }}>
          {title}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>{children}</div>
      </div>
    );
  }

  function Field({ label, value }) {
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: S.textMuted }}>{label}</span>
        <span style={{ fontSize: 13, color: S.textPrimary, fontWeight: 500 }}>{value || '—'}</span>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '24px 32px' }}>
      {activeTab === 'Overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          <Section title={t('screens.policiesScreen.detail.sectionPolicyholder')}>
            <Field label={t('screens.policiesScreen.detail.fieldName')} value={policy.policyholder?.name || '—'} />
            <Field label={t('screens.policiesScreen.detail.fieldCompany')} value={policy.policyholder?.company || '—'} />
            <Field label={t('screens.policiesScreen.detail.fieldEmail')} value={policy.policyholder?.email || '—'} />
            <Field label={t('screens.policiesScreen.detail.fieldPhone')} value={policy.policyholder?.phone || '—'} />
            <Field label={t('screens.policiesScreen.detail.fieldCanton')} value={policy.canton || '—'} />
          </Section>
          <Section title={t('screens.policiesScreen.detail.sectionCoverage')}>
            <Field label={t('screens.policiesScreen.detail.fieldLineOfBusiness')} value={lob} />
            <Field label={t('screens.policiesScreen.detail.fieldSumInsured')} value={formatCurrency(policy.coverageLimit || 0)} />
            <Field label={t('screens.policiesScreen.detail.fieldDeductible')} value={formatCurrency(policy.deductible || 0)} />
            <Field label={t('screens.policiesScreen.detail.fieldCurrency')} value={policy.currency || 'CHF'} />
          </Section>
          <Section title={t('screens.policiesScreen.detail.sectionDates')}>
            <Field label={t('screens.policiesScreen.detail.fieldStartDate')} value={policy.startDate ? formatDate(policy.startDate) : '—'} />
            <Field label={t('screens.policiesScreen.detail.fieldEndDate')} value={policy.endDate ? formatDate(policy.endDate) : '—'} />
            <Field label={t('screens.policiesScreen.detail.fieldIssued')} value={policy.createdAt ? formatDate(policy.createdAt) : '—'} />
            <Field label={t('screens.policiesScreen.detail.fieldLastUpdated')} value={policy.updatedAt ? formatDate(policy.updatedAt) : '—'} />
          </Section>
          <Section title={t('screens.policiesScreen.detail.sectionBrokerCarrier')}>
            <Field label={t('screens.policiesScreen.detail.fieldBroker')} value={policy.brokerName || '—'} />
            <Field label={t('screens.policiesScreen.detail.fieldRiskCarrier')} value={policy.carrierName || '—'} />
            {/* Commission is a commissions-module fact. On a tenant without
                the module it can only ever render an em dash, which reads as
                "no commission on this policy" rather than "this tenant does
                not run commissions". Same rule as the sidebar and the routes. */}
            {commissionsEnabled && (
              <Field
                label={t('screens.policiesScreen.detail.fieldCommission')}
                value={policy.appliedCommissionRate != null
                  ? `${policy.appliedCommissionRate}%`
                  : (policy.commissionPct != null ? `${policy.commissionPct}%` : '—')}
              />
            )}
          </Section>

          {policy.lineOfBusiness === 'surplus_lines' && (
            <Section title={t('screens.policiesScreen.detail.sectionSurplusLines')}>
              <Field label={t('screens.policiesScreen.detail.fieldState')} value={policy.state ? `${STATE_NAMES[policy.state] || policy.state} (${policy.state})` : '—'} />
              <Field label={t('screens.policiesScreen.detail.fieldCarrierName')} value={policy.carrierName || '—'} />
              <Field label={t('screens.policiesScreen.detail.fieldCarrierType')} value={policy.carrierType === 'non_admitted' ? t('screens.policiesScreen.detail.fieldCarrierNonAdmitted') : policy.carrierType || '—'} />
              <Field label={t('screens.policiesScreen.detail.fieldPremiumTax')} value={policy.premiumTax != null ? `$${formatNumber(policy.premiumTax)}${policy.premiumTaxRate ? ` (${(policy.premiumTaxRate * 100).toFixed(2)}%)` : ''}` : '—'} />
              {policy.filingStatus && <Field label={t('screens.policiesScreen.detail.fieldFilingStatus')} value={policy.filingStatus.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} />}
              {policy.eligibilityStatus && <Field label={t('screens.policiesScreen.detail.fieldEligibilityStatus')} value={policy.eligibilityStatus.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} />}
            </Section>
          )}
        </div>
      )}
      {activeTab === 'Claims' && (
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: S.textPrimary, marginBottom: 16 }}>{t('screens.policiesScreen.detail.associatedClaims')}</div>
          {(policy.claims || []).length === 0 ? (
            <div style={{ padding: '40px 0', textAlign: 'center', color: S.textMuted, fontSize: 13 }}>{t('screens.policiesScreen.detail.noClaims')}</div>
          ) : (
            (policy.claims || []).map((c, i) => (
              <div
                key={c._id || i}
                onClick={() => history.push(`/claims/${c._id}`)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '14px 18px',
                  background: S.bgCard,
                  border: `1px solid ${S.borderLight}`,
                  borderRadius: 12,
                  marginBottom: 8,
                  cursor: 'pointer',
                }}
                onMouseEnter={e => (e.currentTarget.style.borderColor = '#cbd5e1')}
                onMouseLeave={e => (e.currentTarget.style.borderColor = S.borderLight)}
              >
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: S.burgundy }}>{c.claimNumber || c._id}</div>
                  <div style={{ fontSize: 12, color: S.textMuted, marginTop: 2 }}>{c.status}</div>
                </div>
                {/* `|| 0` turned an ABSENT reserve into a confident "€ 0".
                    When the field is missing the honest answer is that we have
                    no figure, not that the figure is zero — and a genuine zero
                    reserve must still render as € 0. Currency comes from the
                    record where it carries one. */}
                <div style={{ fontSize: 13, fontWeight: 600, color: S.textPrimary }}>
                  {c.reserveAmount == null ? '—' : formatCurrency(c.reserveAmount, c.currency)}
                </div>
              </div>
            ))
          )}
        </div>
      )}
      {activeTab === 'Documents' && (
        <DocumentsTab policyId={policy?._id} policy={policy} S={S} t={t} />
      )}
      {activeTab === 'Audit' && (
        (policy?.auditTrail?.length || 0) === 0 ? (
          <div style={{ padding: '40px 0', textAlign: 'center', color: S.textMuted, fontSize: 13 }}>{t('screens.policiesScreen.detail.auditPlaceholder')}</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${S.borderLight}` }}>
                {['Timestamp', 'User', 'Action', 'Detail', 'IP'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.8 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {policy.auditTrail.map((a, i) => (
                <tr key={a.id || i} style={{ borderBottom: `1px solid ${S.borderLight}`, background: i % 2 === 0 ? '#fff' : S.bgCard }}>
                  {/* toLocaleString() follows the VIEWER's locale and zone,
                      so the same audit row read differently for two colleagues
                      in different countries. formatDateTime resolves the
                      tenant's format and timezone, as every other timestamp
                      on the platform does. */}
                  <td style={{ padding: '12px', fontSize: 12, color: S.textMuted, fontFamily: 'monospace' }}>{formatDateTime(a.time)}</td>
                  <td style={{ padding: '12px', fontSize: 13, fontWeight: 500, color: S.textPrimary }}>{a.user}</td>
                  {/* Derived label primary, stored string kept beneath as
                      evidence — see utils/auditPresentation.js. The stored row
                      is never rewritten. */}
                  <td style={{ padding: '12px', fontSize: 13, color: S.textSecondary }}>
                    {(() => {
                      const { label, raw } = auditActionLabel(t, a.action);
                      return (
                        <>
                          <div>{label}</div>
                          {raw && <div style={{ fontSize: 11, color: S.textMuted, fontFamily: 'monospace', marginTop: 2 }} title={raw}>{raw}</div>}
                        </>
                      );
                    })()}
                  </td>
                  <td style={{ padding: '12px', fontSize: 13, color: S.textSecondary }}>{a.detail}</td>
                  <td style={{ padding: '12px', fontSize: 12, color: S.textMuted, fontFamily: 'monospace' }}>{a.ip || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}
    </div>
  );
};

export default TabContent;
